// DICOM tag constants + typed accessors over the DICOM JSON Model.
//
// A DICOM JSON dataset looks like:
//   { "0020000D": { "vr": "UI", "Value": ["1.2.3"] },
//     "00100010": { "vr": "PN", "Value": [{ "Alphabetic": "Doe^John" }] } }
// These helpers keep the rest of the app free of raw tag strings and the
// Value[]/PersonName quirks.

export const Tag = {
  // Patient
  PatientName: '00100010',
  PatientID: '00100020',
  IssuerOfPatientID: '00100021',
  PatientBirthDate: '00100030',
  PatientSex: '00100040',
  PatientAge: '00101010',
  AdmittingDiagnosesDescription: '00081080',
  OpticalPathSequence: '00480105',
  ObjectiveLensPower: '00480112',
  IlluminationTypeCodeSequence: '00220016',
  SpecimenTypeCodeSequence: '0040059A',
  SpecimenPreparationSequence: '00400610',
  SpecimenPreparationStepContentItemSequence: '00400612',
  ConceptNameCodeSequence: '0040A043',
  ConceptCodeSequence: '0040A168',
  RequestAttributesSequence: '00400275',
  RequestedProcedureCodeSequence: '00321064',
  CodeValue: '00080100',
  // Study
  StudyInstanceUID: '0020000D',
  StudyDate: '00080020',
  StudyTime: '00080030',
  StudyID: '00200010',
  AccessionNumber: '00080050',
  StudyDescription: '00081030',
  ModalitiesInStudy: '00080061',
  ReferringPhysicianName: '00080090',
  NumberOfStudyRelatedSeries: '00201206',
  NumberOfStudyRelatedInstances: '00201208',
  // Series
  SeriesInstanceUID: '0020000E',
  SeriesNumber: '00200011',
  Modality: '00080060',
  SeriesDescription: '0008103E',
  SeriesDate: '00080021',
  SeriesTime: '00080031',
  NumberOfSeriesRelatedInstances: '00201209',
  // Specimen / Container
  ContainerIdentifier: '00400512',
  IssuerOfTheContainerIdentifierSequence: '00400513',
  LocalNamespaceEntityID: '00400031',
  SpecimenDescriptionSequence: '00400560',
  SpecimenIdentifier: '00400551',
  SpecimenUID: '00400554',
  SpecimenShortDescription: '00400600',
  PrimaryAnatomicStructureSequence: '00082228',
  CodeMeaning: '00080104',
  // Instance
  SOPInstanceUID: '00080018',
  SOPClassUID: '00080016',
  ImageType: '00080008',
  InstanceNumber: '00200013',
  Rows: '00280010',
  Columns: '00280011',
  NumberOfFrames: '00280008',
  TransferSyntaxUID: '00020010',
  FrameOfReferenceUID: '00200052',
  // Pixel encoding and grayscale display
  PhotometricInterpretation: '00280004',
  BitsAllocated: '00280100',
  BitsStored: '00280101',
  WindowCenter: '00281050',
  WindowWidth: '00281051',
  WindowCenterWidthExplanation: '00281055',
  RescaleIntercept: '00281052',
  RescaleSlope: '00281053',
  SmallestImagePixelValue: '00280106',
  LargestImagePixelValue: '00280107',
  // Image Histogram module (PS3.3 C.11.5): an X-ray's base level carries one
  // (convert sources/radiograph.py), which the window box draws.
  HistogramSequence: '00603000',
  HistogramNumberOfBins: '00603002',
  HistogramFirstBinValue: '00603004',
  HistogramBinWidth: '00603008',
  HistogramData: '00603020',
  // X-ray acquisition (DX Image and X-Ray Acquisition modules), carried onto a
  // converted X-ray's pyramid (convert sources/radiograph.py)
  PresentationIntentType: '00080068',
  PostprocessingFunction: '00185021',
  KVP: '00180060',
  ExposureTime: '00181150',
  XRayTubeCurrent: '00181151',
  XRayTubeCurrentInuA: '00188151',
  Exposure: '00181152',
  ExposureInuAs: '00181153',
  DistanceSourceToDetector: '00181110',
  DistanceSourceToPatient: '00181111',
  EstimatedRadiographicMagnificationFactor: '00181114',
  ImagerPixelSpacing: '00181164',
  ViewPosition: '00185101',
  ImageLaterality: '00200062',
  BodyPartExamined: '00180015',
  DetectorType: '00187004',
  DerivationDescription: '00082111',
  OriginalAttributesSequence: '04000561',
  ModifiedAttributesSequence: '04000550',
  // WSI geometry
  TotalPixelMatrixRows: '00480007',
  TotalPixelMatrixColumns: '00480006',
  PixelSpacing: '00280030',
  DimensionOrganizationType: '00209311',
  PerFrameFunctionalGroupsSequence: '52009230',
  SharedFunctionalGroupsSequence: '52009229',
  PixelMeasuresSequence: '00289110',
  // Segmentation
  SegmentSequence: '00620002',
  SegmentIdentificationSequence: '0062000A',
  ReferencedSegmentNumber: '0062000B',
  // Equipment (General Equipment Module)
  Manufacturer: '00080070',
  ManufacturerModelName: '00081090',
  DeviceSerialNumber: '00181000',
  SoftwareVersions: '00181020',
  InstitutionName: '00080080',
  StationName: '00081010',
} as const;

/** A single DICOM JSON dataset (tag -> element). */
export type DicomJson = Record<string, DicomElement | undefined>;

export interface DicomElement {
  vr: string;
  Value?: unknown[];
  BulkDataURI?: string;
  InlineBinary?: string;
}

export function getStrings(ds: DicomJson | undefined, tag: string): string[] {
  const el = ds?.[tag];
  if (!el?.Value) return [];
  return el.Value.map((v) => (typeof v === 'string' ? v : String(v)));
}

export function getString(ds: DicomJson | undefined, tag: string): string | undefined {
  return getStrings(ds, tag)[0];
}

export function getNumber(ds: DicomJson | undefined, tag: string): number | undefined {
  const el = ds?.[tag];
  const v = el?.Value?.[0];
  if (v === undefined || v === null) return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/** PersonName values are objects like { Alphabetic: "Doe^John" }. */
export function getPersonName(ds: DicomJson | undefined, tag: string): string | undefined {
  const v = ds?.[tag]?.Value?.[0];
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    const pn = v as { Alphabetic?: string; Ideographic?: string; Phonetic?: string };
    return pn.Alphabetic ?? pn.Ideographic ?? pn.Phonetic;
  }
  return String(v);
}

/** Format a DICOM person name ("Doe^John^A") for display ("John A Doe"). */
export function formatPersonName(raw: string | undefined): string {
  if (!raw) return '';
  const [family, given, middle] = raw.split('^');
  return [given, middle, family].filter(Boolean).join(' ').trim() || raw;
}

/** Format a DICOM date ("20260616") as "06/16/2026" (mm/dd/yyyy). */
export function formatDicomDate(raw: string | undefined): string {
  if (!raw || raw.length < 8) return raw ?? '';
  return `${raw.slice(4, 6)}/${raw.slice(6, 8)}/${raw.slice(0, 4)}`;
}

/** Format a DICOM time ("143005.123") as "14:30:05". */
export function formatDicomTime(raw: string | undefined): string {
  if (!raw) return '';
  const s = raw.replace(/[^0-9]/g, '');
  if (s.length < 4) return raw;
  const hh = s.slice(0, 2);
  const mm = s.slice(2, 4);
  const ss = s.slice(4, 6);
  return ss ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`;
}

/** Format a DICOM time ("143005") as 12-hour without seconds ("2:30 PM"). */
export function formatDicomTimeShort(raw: string | undefined): string {
  if (!raw) return '';
  const s = raw.replace(/[^0-9]/g, '');
  if (s.length < 4) return raw;
  let h = Number(s.slice(0, 2));
  const mm = s.slice(2, 4);
  const ampm = h < 12 ? 'AM' : 'PM';
  h = h % 12 || 12;
  return `${h}:${mm} ${ampm}`;
}

/** Patient age: the DICOM PatientAge value ("045Y") if present, otherwise
 *  derived from PatientBirthDate. Returns e.g. "45 y" or undefined. */
export function patientAge(ds: DicomJson | undefined): string | undefined {
  const raw = getString(ds, Tag.PatientAge);
  if (raw) {
    const m = raw.match(/^0*(\d+)\s*([DWMY])?$/i);
    if (m) return `${Number(m[1])} ${(m[2] ?? 'Y').toLowerCase()}`;
    return raw;
  }
  const birth = getString(ds, Tag.PatientBirthDate);
  if (!birth || birth.length < 8) return undefined;
  const y = Number(birth.slice(0, 4));
  const mo = Number(birth.slice(4, 6));
  const d = Number(birth.slice(6, 8));
  if (!y || !mo || !d) return undefined;
  const now = new Date();
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < mo || (now.getMonth() + 1 === mo && now.getDate() < d)) age--;
  if (age < 0 || age > 150) return undefined;
  return `${age} y`;
}

/** Sequence (SQ) items — each item is itself a DICOM JSON dataset. */
export function getSequence(ds: DicomJson | undefined, tag: string): DicomJson[] {
  const v = ds?.[tag]?.Value;
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is DicomJson => typeof x === 'object' && x !== null);
}

/** CodeMeaning (0008,0104) of the first item of a code sequence. */
export function getCodeMeaning(ds: DicomJson | undefined, tag: string): string | undefined {
  const item = getSequence(ds, tag)[0];
  return item ? getString(item, Tag.CodeMeaning) : undefined;
}
